import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../hooks/useSubscription';

const SubscriptionFeatureFlags = () => {
  const { 
    featureConfig, 
    loading, 
    error,
    handleUpdateFeatureConfig,
    loadFeatureConfig
  } = useSubscription();
  
  const [config, setConfig] = useState({

    enforcement: 'OFF',

    purchaseCreditsEnabled: true
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load feature config on component mount
  useEffect(() => {
    loadFeatureConfig();
  }, [loadFeatureConfig]);

  // Update local state when feature config changes
  useEffect(() => {
    if (featureConfig) {
      setConfig(featureConfig);
    }
  }, [featureConfig]);

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setSuccessMessage('');

    try {
      await handleUpdateFeatureConfig(config);
      setSuccessMessage('Feature configuration updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Subscription Feature Flags</h1>
        <p className="text-gray-600 mb-8">Manage subscription system features and enforcement</p>
        <div className="text-center text-lg text-gray-600 p-8">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Subscription Feature Flags</h1>
        <p className="text-gray-600 mb-8">Manage subscription system features and enforcement</p>
        <div className="text-center text-lg text-red-600 p-8">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-4xl font-bold text-gray-800 mb-2">Subscription Feature Flags</h1>
      <p className="text-gray-600 mb-8">Manage subscription system features and enforcement</p>

      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-teal-500">
          Feature Configuration
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Subscription System</h3>
              <p className="text-sm text-gray-600">Enable or disable the entire subscription system</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
            </label>
          </div>

          {/* Enforcement Mode */}
          <div className="space-y-2">
            <label className="font-semibold text-gray-800 text-sm">Enforcement Mode:</label>
            <select
              value={config.enforcement}
              onChange={(e) => handleInputChange('enforcement', e.target.value)}
              disabled={!config.enabled}
              className="w-full p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="OFF">Off - No enforcement</option>
              <option value="DRY_RUN">Dry run - Log violations only</option>
              <option value="ADMINS_ONLY">Admins only - Enforce for admin users</option>
              <option value="ALL">All users - Full enforcement</option>
            </select>
            <p className="text-xs text-gray-500">
              Controls how strictly subscription limits are enforced across the system
            </p>
          </div>

          {/* Feature Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Core Features</h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Free Tier</label>
                  <p className="text-sm text-gray-500">Allow users to access free tier plans</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.freeTierEnabled}
                    onChange={(e) => handleInputChange('freeTierEnabled', e.target.checked)}
                    disabled={!config.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 disabled:opacity-60"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Auto-Renew</label>
                  <p className="text-sm text-gray-500">Allow subscriptions to auto-renew</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.autoRenewEnabled}
                    onChange={(e) => handleInputChange('autoRenewEnabled', e.target.checked)}
                    disabled={!config.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 disabled:opacity-60"></div>
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Quota & Credits</h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Storage Quota</label>
                  <p className="text-sm text-gray-500">Enforce storage limits per plan</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.storageQuotaEnabled}
                    onChange={(e) => handleInputChange('storageQuotaEnabled', e.target.checked)}
                    disabled={!config.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 disabled:opacity-60"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="font-medium text-gray-700">Purchase Credits</label>
                  <p className="text-sm text-gray-500">Track monthly free credits</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.purchaseCreditsEnabled}
                    onChange={(e) => handleInputChange('purchaseCreditsEnabled', e.target.checked)}
                    disabled={!config.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 disabled:opacity-60"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isProcessing}
              className="px-8 py-4 bg-teal-600 text-white border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Updating...' : 'Update Feature Configuration'}
            </button>
          </div>
        </form>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-600 text-white rounded-xl p-6 mt-4">
          <h3 className="text-xl font-semibold mb-2">Success!</h3>
          <p className="text-base opacity-90">{successMessage}</p>
        </div>
      )}

      {/* Current Configuration Display */}
      <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-gray-300">
          Current Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">System Status</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Subscription System:</span>
                <span className={`font-semibold ${config.enabled ? 'text-green-600' : 'text-red-600'}`}>
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Enforcement:</span>
                <span className="font-semibold text-gray-800">{config.enforcement}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Feature Status</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Free Tier:</span>
                <span className={`font-semibold ${config.freeTierEnabled ? 'text-green-600' : 'text-red-600'}`}>
                  {config.freeTierEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Auto-Renew:</span>
                <span className={`font-semibold ${config.autoRenewEnabled ? 'text-green-600' : 'text-red-600'}`}>
                  {config.autoRenewEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Storage Quota:</span>
                <span className={`font-semibold ${config.storageQuotaEnabled ? 'text-green-600' : 'text-red-600'}`}>
                  {config.storageQuotaEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Purchase Credits:</span>
                <span className={`font-semibold ${config.purchaseCreditsEnabled ? 'text-green-600' : 'text-red-600'}`}>
                  {config.purchaseCreditsEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionFeatureFlags;
